'use client';

import React, { useEffect, useCallback, useState, useMemo } from 'react';
// import circleRightArrow from '../../assets/images/circle-right-arrow.svg';
import useEmblaCarousel from 'embla-carousel-react';
import { AirRingColors } from 'content/products/ring';
import { CaretUp } from '../../assets/icons';
import { useWindowSize } from '../../hooks/dom/useWindowSize';
import { CustomImage as Image, VideoPlayer } from '../shared';
import { PHONE_MAX_WIDTH_IN_PX } from '../theme';
import { debounce } from 'utils/performance/debounce';
import styles from './Carousel.module.css';

// interface GMTResponse {
//   gmtDate: Date;
//   gmtTime: number;
// }

const CaptionFlexItems = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.captionFlexItems}>{children}</div>
);

const captions = [
  {
    title: '',
    description: '',
    flexItems: true,
    items: (
      <>
        <CaptionFlexItems>
          <p>
            <span>15-day wear</span> for extended performance
          </p>
          <p>
            <span>Bluetooth-enabled syncing.</span> No more manual NFC scans
          </p>
        </CaptionFlexItems>
        <CaptionFlexItems>
          <p>
            <span>Uninterrupted data updates,</span> even on day 15
          </p>
          <p>
            <span>Full visibility</span> within minutes after a single sync
          </p>
        </CaptionFlexItems>
      </>
    ),
  },
  {
    title: 'Fitness and sports compatible',
    description:
      'Comfortable and minimal form factor to support intense and leisurely ',
  },
  {
    title: 'Hypoallergenic patches',
    description:
      'Hypoallergenic and breathable patches to support all skin types',
  },
  {
    title: 'Personalised food suggestions',
    description:
      'Based on unique glucose metabolism insights, identify foods that give you a stable glucose response',
  },
];

export const Carousal = ({
  items,
  showCaptions = false,
}: {
  items: ReadonlyArray<Record<string, string | boolean | undefined>>;
  selectedColor?: AirRingColors;
  showCaptions?: boolean;
}) => {
  const [windowWidth, setWindowWidth] = useState(1024);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: windowWidth <= PHONE_MAX_WIDTH_IN_PX ? 'center' : 'start',
    slidesToScroll: 1,
  });

  const [activeSlide, setActiveSlide] = useState<number | string | undefined>(
    0,
  );

  const _windowSize: Record<string, number | undefined> = useWindowSize();

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const debouncedReInit = useMemo(
    () =>
      debounce((newWidth: number) => {
        if (emblaApi) {
          emblaApi.reInit({
            loop: true,
            align: newWidth <= PHONE_MAX_WIDTH_IN_PX ? 'center' : 'start',
            slidesToScroll: 1,
          });
        }
      }, 150),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (window) {
      setWindowWidth(window.innerWidth);
    }

    if (document) {
      const images = document.getElementsByTagName('img');
      for (const item of images) {
        item.onload = () => {
          item.style.background = 'transparent';
        };
      }
    }
  }, []);

  useEffect(() => {
    if (windowWidth) {
      // Only reinit if we crossed a meaningful breakpoint threshold
      const currentIsMobile = windowWidth <= PHONE_MAX_WIDTH_IN_PX;
      const prevIsMobile = windowWidth - 50 <= PHONE_MAX_WIDTH_IN_PX; // Add hysteresis

      if (currentIsMobile !== prevIsMobile) {
        debouncedReInit(windowWidth);
      }
    }
  }, [windowWidth, debouncedReInit]);

  return (
    <div className={styles.root}>
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.embla__container}>
          {items?.map(
            (
              element: Record<string, string | boolean | undefined>,
              index,
            ) => {
              return (
                <div className={styles.embla__slide} key={index}>
                  <div className={styles['carousal-img-container']}>
                    {!element.video && (
                      <>
                        <div className={styles['element-img']}>
                          {/* Art-directed via CSS orientation (not JS
                              windowSize, which is 0 on the server and caused
                              the wrong crop to render first then swap). Both
                              are emitted server-side; CSS shows the right one
                              for the viewport from first paint. */}
                          <Image
                            src={element.src as string}
                            alt={element.alt as string}
                            fill
                            className={`custom-img m1-img-landscape`}
                            style={{
                              objectFit: 'contain',
                              objectPosition: element.objectPositionBottom
                                ? 'bottom'
                                : undefined,
                            }}
                            priority={index <= 1 ? true : false}
                            sizes="(min-width: 768px) 50vw, 100vw"
                          />
                          <Image
                            src={
                              (element.srcResponsive ?? element.src) as string
                            }
                            alt={element.alt as string}
                            fill
                            className={`custom-img m1-img-portrait`}
                            style={{
                              objectFit: 'contain',
                              objectPosition: element.objectPositionBottom
                                ? 'bottom'
                                : undefined,
                            }}
                            priority={index <= 1 ? true : false}
                            sizes="(min-width: 768px) 50vw, 100vw"
                          />
                        </div>
                        {showCaptions ? (
                          <div className={styles.content}>
                            <h3>{captions[index]?.title}</h3>
                            <p>{captions[index]?.description}</p>
                            <>{captions[index]?.items}</>
                          </div>
                        ) : null}
                      </>
                    )}
                    {element.video && (
                      <div className={styles['video-container']}>
                        <VideoPlayer
                          className="video-player"
                          playable={activeSlide == index}
                          videoProps={{
                            src: element.src as string,
                            poster: element.poster as string,
                            preload: 'true',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>
      <button
        className={styles.embla__prev}
        onClick={scrollPrev}
        aria-label="Previous slide"
      >
        <div className={styles['circle-blur-btn']}>
          <CaretUp style={{ transform: 'rotate(-90deg)' }} fill="white" />
        </div>
      </button>
      <button
        className={styles.embla__next}
        onClick={scrollNext}
        aria-label="Next slide"
      >
        <div className={styles['circle-blur-btn']}>
          <CaretUp style={{ transform: 'rotate(90deg)' }} fill="white" />
        </div>
      </button>
      {/* <div className="flex-center">
          <div className="slids-thumbs-container">
            {items?.map(
              (
                element: Record<string, string | boolean | undefined>,
                index: number,
              ) => {
                return (
                  <Image
                    src={element.thumb as string}
                    alt={element.alt as string}
                    layout={`fixed`}
                    width={75}
                    height={48}
                    aria-hidden="true"
                    key={index}
                    onClick={() => handleSlideChange(index)}
                    className={activeSlide == index ? 'active' : ''}
                  />
                );
              },
            )}
          </div>
        </div> */}
    </div>
  );
};
